import Chart from './Chart';
import LeakerChart from './LeakerChart';
import PieChart from './PieChart';
import ParetoChart from './ParetoChart';
import PieChartStacked from './PieChartStacked';
import ChartStandardDistribution from './ChartStandardDistribution';
import ChartSummaryDistribution from './ChartSummaryDistribution';
import DatasetBarChart from './DatasetBarChart';

const IS_CI_ENABLED = true;

export const useCharts = () => {
  const ChartCollection1: React.FC<{ allResults: any[] }> = ({
    allResults,
  }) => {
    return (
      <div className="grid-container">
        {allResults.map((result, index) => (
          <Chart
            key={index}
            type={result.type}
            emissions={result.emissions}
            ci={IS_CI_ENABLED}
          />
        ))}
      </div>
    );
  };

  const ChartCollection2: React.FC<{
    allResults: any[];
    tabType: string;
    compact?: boolean;
  }> = ({ allResults, tabType, compact = false }) => {
    return (
      <div className="grid-container">
        <LeakerChart
          emissions={allResults}
          options={{
            title: 'Leaker Emission Factor (kg/h)',
            yLabel: 'Emissions (kg/h)',
            type: tabType,
          }}
          compact={compact}
          ci={IS_CI_ENABLED}
        />
      </div>
    );
  };

  const ChartCollection3: React.FC<{
    allResults: any[];
    tabType: string;
    compact?: boolean;
  }> = ({ allResults, tabType, compact = false }) => {
    return (
      <div className="grid-container">
        <LeakerChart
          emissions={allResults}
          options={{
            title: 'Population Emission Factor (kg/h)',
            yLabel: 'Population EF (kg/h)',
            type: tabType,
          }}
          compact={compact}
          ci={IS_CI_ENABLED}
        />
      </div>
    );
  };

  const ChartCollection4: React.FC<{
    allResults: any[];
    type?: string;
    dataType?: string;
    compact?: boolean;
  }> = ({ allResults, type = 'bar', dataType = 'equip', compact = false }) => {
    return (
      <div className="grid-container">
        {type === 'stacked' ? (
          <PieChartStacked results={allResults} compact={compact} />
        ) : (
          <PieChart
            dataType={dataType}
            results={allResults}
            compact={compact}
          />
        )}
      </div>
    );
  };

  const ChartCollection5: React.FC<{
    dataType?: string;
    allResults: any[];
    title: string;
    compact?: boolean;
  }> = ({ dataType = 'equip', allResults, title, compact = false }) => {
    return (
      <div className="grid-container">
        <ParetoChart
          type={dataType}
          results={allResults}
          title={title}
          compact={compact}
        />
      </div>
    );
  };

  const ChartCollection6: React.FC<{
    allResults: any[];
    title: string;
    logAxis?: boolean;
    compact?: boolean;
    min: number;
    max: number;
  }> = ({ allResults, title, logAxis = false, compact = false, min, max }) => {
    return (
      <div className="chart-quad">
        <ChartSummaryDistribution
          emissions={allResults}
          title={title}
          minLogRateGlobal={min === 0 ? 0.1 : min}
          maxLogRateGlobal={max}
          logAxis={logAxis}
          compact={compact}
          ci={IS_CI_ENABLED}
        />
      </div>
    );
  };

  const ChartCollection7: React.FC<{
    allResults: any[];
    tabType?: string;
    compact?: boolean;
  }> = ({ allResults, tabType = 'equip', compact = false }) => {
    const allLogEmissionRates = allResults.flatMap((result) =>
      result.emissions
        .map(
          (e: { [x: string]: any }) => e['emission_rate_kgh'] || e['emissions'],
        )
        .filter((rate: number) => rate >= 0),
    );

    const minLogRateGlobal = Math.min(...allLogEmissionRates);
    const maxLogRateGlobal = Math.max(...allLogEmissionRates);

    return (
      <div className="grid-container">
        {allResults.map((result, index) => (
          <ChartStandardDistribution
            key={index}
            type={result.type}
            emissions={result.emissions}
            minLogRateGlobal={minLogRateGlobal === 0 ? 0.01 : minLogRateGlobal}
            maxLogRateGlobal={maxLogRateGlobal}
            tabType={tabType}
            compact={compact}
            logAxis={true}
            ci={IS_CI_ENABLED}
          />
        ))}
      </div>
    );
  };

  const ChartCollection8: React.FC<{
    allResults: any;
    tabType: string;
    compact?: boolean;
    title?: string;
  }> = ({
    allResults,
    tabType,
    compact = false,
    title = 'Population Emission Factor (kg/h)',
  }) => {
    return (
      <div className="grid-container">
        <DatasetBarChart
          emissions={allResults}
          options={{
            title,
            yLabel: 'kg/h',
            type: tabType,
          }}
          compact={compact}
        />
      </div>
    );
  };

  return {
    ChartCollection1,
    ChartCollection2,
    ChartCollection3,
    ChartCollection4,
    ChartCollection5,
    ChartCollection6,
    ChartCollection7,
    ChartCollection8,
  };
};
